/**
 * Türkçe varsayılan doğrulama iletileri (ürün turu 08.10.2026). Şemada ileti verilmemiş kurallarda Zod'un İngilizce
 * varsayılanı kullanıcıya ulaşıyordu (ör. form belgesinde "Konum: Number must be less than or equal to 90"). Bu eşleyici
 * paylaşılan paket yüklenince bir kez kurulur (API + web aynı paketi yükler); şemadaki özel ileti her zaman önceliklidir
 * (Zod kuralı: `.max(90, '...')` iletisi eşleyiciye düşmez). İfadeler içe aktarma motorunun mevcut çevirisiyle
 * (`apps/api/src/services/import-engine.ts` `trIssue`) aynıdır; içe aktarma hata raporu değişmez.
 */
import { z } from 'zod';
const n = (v) => v.toString();
/**
 * Zorunlu alan iletisinin TEK yazımı (K-M7-50.5, v3.1 A7): API doğrulaması, içe aktarma hata raporu, herkese açık formlar
 * ve arayüz alan iletileri aynı metni kullanır (önceden "zorunlu", "zorunlu alan", "bu alan zorunlu", "Bu alan
 * zorunludur." gibi farklı yazımlar vardı).
 */
export const REQUIRED_FIELD_MESSAGE = 'Bu alan zorunlu';
/**
 * Zod 4 eşleyicisi (zod 3 → 4 güvenlik geçişi, GHSA-mrhx-c7xc-m3w5). İletiler zod 3 sürümündekiyle AYNIDIR; yalnız zod 4'ün
 * sorun biçimi okunur: `invalid_string`/`validation` → `invalid_format`/`format`, `too_small`/`too_big` `type` → `origin`,
 * `invalid_enum_value`/`invalid_literal` → `invalid_value`, `invalid_union_discriminator` → `invalid_union`,
 * `not_multiple_of` `multipleOf` → `divisor`, `received === 'undefined'` → `input === undefined`. Zod 4'te ayrı kodu kalmayan
 * zod 3 durumları aynı metne eşlenir: geçersiz Date (`invalid_date` → `invalid_type` + Date girdi) "geçersiz tarih", sonsuz
 * sayı (`not_finite` → `invalid_type` + sonlu olmayan sayı) "geçersiz sayı"; eksik enum değeri (zod 3'te `invalid_type`,
 * zod 4'te `invalid_value`) yine "Bu alan zorunlu". Şemadaki özel ileti önceliklidir (zod 4: iletisi olan sorun eşleyiciye
 * hiç düşmez); iletisiz `custom` ve diğer kodlar "geçersiz değer".
 */
export const trErrorMap = (issue) => {
    switch (issue.code) {
        case 'invalid_type': {
            const input = issue.input;
            if (input === undefined)
                return { message: REQUIRED_FIELD_MESSAGE };
            if (issue.expected === 'date' && input instanceof Date)
                return { message: 'geçersiz tarih' };
            if ((issue.expected === 'number' || issue.expected === 'int') &&
                typeof input === 'number' &&
                !Number.isNaN(input) &&
                !Number.isFinite(input))
                return { message: 'geçersiz sayı' };
            return { message: 'geçersiz değer' };
        }
        case 'too_small':
            if (issue.origin === 'string')
                return {
                    message: Number(issue.minimum) <= 1 ? 'boş olamaz' : `en az ${n(issue.minimum)} karakter olmalı`,
                };
            if (issue.origin === 'number' || issue.origin === 'int' || issue.origin === 'bigint')
                return {
                    message: issue.inclusive
                        ? `en az ${n(issue.minimum)}`
                        : `${n(issue.minimum)} değerinden büyük olmalı`,
                };
            if (issue.origin === 'array' || issue.origin === 'set')
                return {
                    message: Number(issue.minimum) <= 1
                        ? 'en az bir değer gerekli'
                        : `en az ${n(issue.minimum)} değer gerekli`,
                };
            return { message: 'değer çok küçük' };
        case 'too_big':
            if (issue.origin === 'string')
                return { message: `en çok ${n(issue.maximum)} karakter` };
            if (issue.origin === 'number' || issue.origin === 'int' || issue.origin === 'bigint')
                return {
                    message: issue.inclusive
                        ? `en çok ${n(issue.maximum)}`
                        : `${n(issue.maximum)} değerinden küçük olmalı`,
                };
            if (issue.origin === 'array' || issue.origin === 'set')
                return { message: 'çok fazla değer' };
            return { message: 'değer çok büyük' };
        case 'invalid_format':
            if (issue.format === 'email')
                return { message: 'geçersiz e-posta' };
            if (issue.format === 'url')
                return { message: 'geçersiz adres' };
            if (issue.format === 'datetime')
                return { message: 'geçersiz tarih-saat' };
            if (issue.format === 'date')
                return { message: 'geçersiz tarih' };
            return { message: 'geçersiz değer' };
        case 'not_multiple_of':
            return { message: `${n(issue.divisor)} katı olmalı` };
        case 'unrecognized_keys':
            return { message: `tanınmayan alan: ${issue.keys.join(', ')}` };
        case 'invalid_value':
            // zod 3'te eksik enum değeri `invalid_type` (received undefined) idi → zorunlu alan iletisi korunur
            if (issue.input === undefined &&
                issue.inst?._zod.def?.type === 'enum')
                return { message: REQUIRED_FIELD_MESSAGE };
            return { message: 'geçersiz değer' };
        default:
            // custom (iletisiz), invalid_union, invalid_key, invalid_element
            return { message: 'geçersiz değer' };
    }
};
z.config({ customError: trErrorMap });
//# sourceMappingURL=zod-tr.js.map